import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatTournamentDate } from '../DateSelector';

const HomeTabPreview = ({ data, showRegistration, onTabChange }) => {
  const navigate = useNavigate();
  
  // Helper function to get display value or return null for conditional rendering
  const getDisplayValue = (value) => typeof value === 'string' && value.trim() !== '' ? value : null;

  // Check if Home tab has any content
  const hasContent = () => {
    return getDisplayValue(data.eventTitle) || 
           getDisplayValue(data.startDate) || 
           getDisplayValue(data.endDate) ||
           getDisplayValue(data.venueName) ||
           getDisplayValue(data.venueAddress);
  };

  if (!hasContent()) {
    return null;
  }

  // Get primary color with fallback
  const primaryColor = data.primaryColorHex || '#3B82F6';
  const organizerContent = data.homeOrganizerSection && data.homeOrganizerSection.trim() !== ''
    ? data.homeOrganizerSection
    : null;

  const parseOrganizerInline = (text, keyPrefix) => {
    const tokens = text.split(/(\*\*[^*]+\*\*|_[^_]+_|\*[^*]+\*)/);
    return tokens.filter(token => token !== '').map((token, index) => {
      if (token.startsWith('**') && token.endsWith('**')) {
        return <strong key={`${keyPrefix}-b-${index}`}>{token.slice(2, -2)}</strong>;
      }
      if ((token.startsWith('_') && token.endsWith('_')) || (token.startsWith('*') && token.endsWith('*'))) {
        return <em key={`${keyPrefix}-i-${index}`}>{token.slice(1, -1)}</em>;
      }
      return token;
    });
  };

  const renderOrganizerContent = (content) => {
    const lines = content.split(/\r?\n/);
    return lines.map((line, index) => {
      const trimmed = line.trim();
      if (trimmed === '') {
        return <div key={`organizer-spacer-${index}`} className="h-2" />;
      }

      if (trimmed.startsWith('### ')) {
        return (
          <h5 key={`organizer-h3-${index}`} className="text-base font-semibold text-gray-800">
            {parseOrganizerInline(trimmed.slice(4), `org-${index}`)}
          </h5>
        );
      }

      if (trimmed.startsWith('## ')) {
        return (
          <h4 key={`organizer-h2-${index}`} className="text-lg font-semibold text-gray-900">
            {parseOrganizerInline(trimmed.slice(3), `org-${index}`)}
          </h4>
        );
      }

      if (trimmed.startsWith('# ')) {
        return (
          <h3 key={`organizer-h1-${index}`} className="text-xl font-bold text-gray-900">
            {parseOrganizerInline(trimmed.slice(2), `org-${index}`)}
          </h3>
        );
      }

      return (
        <p key={`organizer-p-${index}`} className="text-sm sm:text-base leading-relaxed text-gray-700">
          {parseOrganizerInline(trimmed, `org-${index}`)}
        </p>
      );
    });
  };

  // Handle register button click
  const handleRegisterClick = () => {
    const useInternal = data.useInternalRegistration !== false; // default to true
    const eventId = data.eventId || data.id;
    if (useInternal) {
      navigate(`/register?eventId=${eventId}`);
    } else if (data.registrationLink && data.registrationLink.trim() !== '') {
      let link = data.registrationLink.trim();
      if (!/^https?:\/\//i.test(link)) {
        link = 'https://' + link;
      }
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="max-w-4xl mx-auto overflow-hidden">
      
      {/* Event Details Content */}
      <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
        
        {/* Basic Information Grid */}
        <div className="bg-chess-cream rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 mb-6 max-w-full sm:max-w-3xl mx-auto">
          <h2 className="text-lg font-semibold text-center mb-4 text-gray-900">Chess Tournament Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Tournament Dates */}
            {(getDisplayValue(data.startDate) || getDisplayValue(data.endDate)) && (
              <div className="md:col-span-2">
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280'
                }}>Tournament Dates</h3>
                <h5 style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#111827'
                }}>
                  {formatTournamentDate(data.startDate, data.endDate)}
                </h5>
              </div>
            )}

            {/* Venue Name (Location) */}
            {(getDisplayValue(data.venueName) || getDisplayValue(data.city)) && (
              <div>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280'
                }}>Location</h3>
                <p style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#111827'
                }}>{getDisplayValue(data.city) ? data.city : data.venueName}</p>
              </div>
            )}

            {/* Address */}
            {getDisplayValue(data.venueAddress) && (
              <div>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280'
                }}>Address</h3>
                <p style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#111827'
                }}>{data.venueAddress}</p>
              </div>
            )}

            {/* Prize Fund (Solo Row) */}
            {getDisplayValue(data.prizeFund) && (
              <div className="md:col-span-2">
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280'
                }}>Prize Fund</h3>
                <p style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#FF6B35' // Orange color
                }}>{data.prizeFund}</p>
              </div>
            )}

            {/* Payment Details */}
            {getDisplayValue(data.paymentInfo) && (
              <div>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280'
                }}>Payment Details</h3>
                <p style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#111827'
                }}>{data.paymentInfo}</p>
              </div>
            )}

            {/* Registration Fee - Smart display with early bird pricing */}
            {(() => {
              // Helper function to get current pricing tier (same logic as registration form)
              const getCurrentPricingTier = (section) => {
                if (!section.pricingTiers || section.pricingTiers.length === 0) {
                  return null;
                }

                const currentDate = new Date();
                // Sort tiers by end date to find the earliest active one
                const sortedTiers = [...section.pricingTiers].sort((a, b) => {
                  if (!a.endDate) return 1;
                  if (!b.endDate) return -1;
                  return new Date(a.endDate) - new Date(b.endDate);
                });

                // Find the first tier that's still active (end date is in the future or today)
                const activeTier = sortedTiers.find(tier => {
                  if (!tier.endDate) return false;
                  const endDate = new Date(tier.endDate);
                  return endDate >= currentDate;
                });

                return activeTier || null;
              };

              // Helper function to get current price for a section
              const getCurrentSectionPrice = (section) => {
                const currentTier = getCurrentPricingTier(section);
                
                // Get the tier price - check both fee and label fields for backward compatibility
                let currentPrice = 0;
                if (currentTier) {
                  // Try fee field first
                  if (currentTier.fee && parseFloat(currentTier.fee) > 0) {
                    currentPrice = parseFloat(currentTier.fee);
                  }
                  // Fall back to label field if fee is 0 or empty
                  else if (currentTier.label && !isNaN(parseFloat(currentTier.label)) && parseFloat(currentTier.label) > 0) {
                    currentPrice = parseFloat(currentTier.label);
                  }
                }
                
                // If no valid tier price found, use section base fee
                if (currentPrice === 0 && section.baseFee && parseFloat(section.baseFee) > 0) {
                  currentPrice = parseFloat(section.baseFee);
                }
                
                return currentPrice;
              };

              // Check if we have tournament sections with pricing
              if (data.tournamentSections && data.tournamentSections.length > 0) {
                const currency = data.currency || '$';
                
                // Get the first section with pricing tiers to show the pricing structure
                const sectionWithTiers = data.tournamentSections.find(section => 
                  section.pricingTiers && section.pricingTiers.length > 0
                );
                
                if (sectionWithTiers) {
                  // Show detailed pricing breakdown
                  const currentDate = new Date();
                  
                  // Sort tiers by end date chronologically
                  const sortedTiers = [...sectionWithTiers.pricingTiers].sort((a, b) => {
                    if (!a.endDate) return 1;
                    if (!b.endDate) return -1;
                    return new Date(a.endDate) - new Date(b.endDate);
                  });
                  
                  // Create pricing breakdown in improved format
                  const pricingParts = [];
                  
                  // Add early bird tiers
                  const earlyBirdTiers = [];
                  sortedTiers.forEach(tier => {
                    if (tier.endDate) {
                      const endDate = new Date(tier.endDate);
                      const isActive = endDate >= currentDate;
                      const price = tier.fee && parseFloat(tier.fee) > 0 ? 
                        parseFloat(tier.fee) : 
                        (tier.label && !isNaN(parseFloat(tier.label)) ? parseFloat(tier.label) : 0);
                      
                      if (price > 0) {
                        const dateStr = endDate.toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric' 
                        });
                        earlyBirdTiers.push(`$${price} before ${dateStr}${isActive ? ' ⭐' : ''}`);
                      }
                    }
                  });
                  
                  // Format the pricing display in correct order
                  
                  // 1. Standard Price first
                  const baseFee = parseFloat(sectionWithTiers.baseFee) || 0;
                  if (baseFee > 0) {
                    pricingParts.push(`$${baseFee} Standard Price`);
                  }
                  
                  // 2. Early bird tiers
                  if (earlyBirdTiers.length > 0) {
                    pricingParts.push(...earlyBirdTiers);
                  }
                  
                  // 3. Play up option last
                  if (data.enablePlayUp && data.playUpFee) {
                    const playUpFee = parseFloat(data.playUpFee);
                    if (playUpFee > 0) {
                      pricingParts.push(`$${playUpFee} Extra Fee For Playing Up`);
                    }
                  }
                  
                  return (
                    <div className="md:col-span-1">
                      <h3 style={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#6B7280',
                        marginBottom: '12px'
                      }}>Registration Fee</h3>
                      <div className="space-y-2">
                        {pricingParts.map((part, index) => {
                          const isPlayUp = part.includes('play up');
                          const isActive = part.includes('⭐');
                          
                          return (
                            <div 
                              key={index} 
                              className="flex items-center justify-between py-2 px-3 bg-white border border-gray-200 rounded-md"
                            >
                              <span style={{
                                fontSize: '15px',
                                fontWeight: 600,
                                color: isPlayUp ? '#6B7280' : '#111827'
                              }}>
                                {part.replace(' ⭐', '')}
                              </span>
                              {isActive && (
                                <span style={{ fontSize: '16px' }}>⭐</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                } else {
                  // No pricing tiers - show simple pricing
                  const currentPrices = data.tournamentSections
                    .map(section => parseFloat(section.baseFee) || 0)
                    .filter(price => price > 0);
                  
                  if (currentPrices.length === 0) {
                    return null; // No fees to display
                  }
                  
                  const uniquePrices = [...new Set(currentPrices)].sort((a, b) => a - b);
                  
                  let displayText = '';
                  if (uniquePrices.length === 1) {
                    displayText = `$${uniquePrices[0]}`;
                  } else {
                    displayText = `Starting at ${currency}${uniquePrices[0]}`;
                  }
                  
                  // Add play up option
                  if (data.enablePlayUp && data.playUpFee) {
                    const playUpFee = parseFloat(data.playUpFee);
                    if (playUpFee > 0) {
                      displayText += ` (+$${playUpFee} play up)`;
                    }
                  }
                  
                  return (
                    <div className="md:col-span-1">
                      <h3 style={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#6B7280'
                      }}>Registration Fee</h3>
                      <p style={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#10B981'
                      }}>{displayText}</p>
                    </div>
                  );
                }
              }
              
              // Fallback to legacy registration fee field
              if (getDisplayValue(data.registrationFee)) {
                return (
                  <div className="md:col-span-1">
                    <h3 style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#6B7280'
                    }}>Registration Fee</h3>
                    <p style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#10B981' // Brighter green (Tailwind emerald-500)
                    }}>$ {data.registrationFee}</p>
                  </div>
                );
              }
              
              return null;
            })()}

            {/* How to Register Button - Show if registration is available */}
            {showRegistration && (() => {
              const useInternal = data.useInternalRegistration !== false; // default to true
              const hasExternalLink = data.registrationLink && typeof data.registrationLink === 'string' && data.registrationLink.trim() !== '';
              const shouldShowButton = useInternal || hasExternalLink;
              
              if (!shouldShowButton) {
                return null; // Don't render button if no registration method available
              }
              
              return (
                <div className="md:col-span-1">
                  <div className="h-full flex flex-col justify-end">
                    <button
                      onClick={() => {
                        if (onTabChange) {
                          onTabChange('registration');
                        }
                      }}
                      className="w-full py-3 px-4 rounded-lg font-semibold text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2"
                      style={{ 
                        backgroundColor: primaryColor,
                        color: 'white',
                        borderColor: primaryColor,
                        boxShadow: `0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)`,
                        position: 'relative',
                        zIndex: 1
                      }}
                      onMouseEnter={(e) => {
                        // Darken the color slightly on hover and enhance shadow
                        const color = primaryColor;
                        const darkenAmount = 0.1;
                        const rgb = color.match(/\d+/g);
                        if (rgb && rgb.length >= 3) {
                          const r = Math.max(0, parseInt(rgb[0]) - Math.round(255 * darkenAmount));
                          const g = Math.max(0, parseInt(rgb[1]) - Math.round(255 * darkenAmount));
                          const b = Math.max(0, parseInt(rgb[2]) - Math.round(255 * darkenAmount));
                          e.target.style.backgroundColor = `rgb(${r}, ${g}, ${b})`;
                        }
                        e.target.style.boxShadow = `0 12px 24px rgba(0, 0, 0, 0.3), 0 6px 12px rgba(0, 0, 0, 0.2)`;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = primaryColor;
                        e.target.style.boxShadow = `0 8px 16px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)`;
                      }}
                    >
                      How to Register <i className="fa-solid fa-arrow-right ml-1"></i>
                    </button>
                  </div>
                </div>
              );
            })()}

          </div>
        </div>

        {organizerContent && (
          <div className="bg-chess-cream rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 mb-6 max-w-full sm:max-w-3xl mx-auto">
            <h2 className="text-lg font-semibold text-center mb-4 text-gray-900">
              Additional Details
            </h2>
            <div className="space-y-3">
              {renderOrganizerContent(organizerContent)}
            </div>
          </div>
        )}

        {/* Tournament Format Section */}
        {(getDisplayValue(data.timeControl) || getDisplayValue(data.rounds) || getDisplayValue(data.tournamentFormat)) && (
          <div className="bg-chess-cream rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 mb-6 max-w-full sm:max-w-3xl mx-auto">
            <h2 className="text-lg font-semibold text-center mb-4 text-gray-900">Tournament Format</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              
              {/* Time Control */}
              {getDisplayValue(data.timeControl) && (
                <div>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#6B7280'
                  }}>Time Control</h3>
                  <p style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#111827'
                  }}>{data.timeControl}</p>
                </div>
              )}

              {/* Rounds */}
              {typeof data.rounds === 'string' && getDisplayValue(data.rounds) && (
                <div>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#6B7280'
                  }}>Rounds</h3>
                  <p style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#111827'
                  }}>{data.rounds}</p>
                </div>
              )}

              {/* Format */}
              {getDisplayValue(data.tournamentFormat) && (
                <div>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#6B7280'
                  }}>Format</h3>
                  <p style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#111827'
                  }}>{data.tournamentFormat}</p>
                </div>
              )}

              {/* Federation */}
              {getDisplayValue(data.federation) && (
                <div>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#6B7280'
                  }}>Rating System</h3>
                  <p style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#111827'
                  }}>{data.federation}</p>
                </div>
              )}

              {/* Max Players */}
              {getDisplayValue(data.maxPlayers) && (
                <div>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#6B7280'
                  }}>Max Players</h3>
                  <p style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#111827'
                  }}>{data.maxPlayers}</p>
                </div>
              )}

              {/* Sections & Pricing - Unified */}
              {(data.tournamentSections && data.tournamentSections.length > 0) ? (
                <div className="md:col-span-3">
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#6B7280'
                  }}>Sections & Pricing</h3>
                  <div className="space-y-3 mt-2">
                    {data.tournamentSections.map((section, index) => {
                      const currency = data.currency || '$';
                      const baseFee = parseFloat(section.baseFee) || 0;
                      
                      // Get current pricing (earliest available or base fee)
                      let currentPrice = baseFee;
                      let priceLabel = '';
                      
                      if (section.pricingTiers && section.pricingTiers.length > 0) {
                        // Sort tiers by end date to find the earliest one
                        const sortedTiers = [...section.pricingTiers].sort((a, b) => {
                          if (!a.endDate) return 1;
                          if (!b.endDate) return -1;
                          return new Date(a.endDate) - new Date(b.endDate);
                        });
                        
                        // Find the first tier that's still active (end date is in the future)
                        const today = new Date();
                        const activeTier = sortedTiers.find(tier => {
                          if (!tier.endDate) return false;
                          return new Date(tier.endDate) >= today;
                        });
                        
                        if (activeTier) {
                          currentPrice = parseFloat(activeTier.fee) || baseFee;
                          if (currentPrice < baseFee) {
                            priceLabel = 'Early Bird Price: ';
                          }
                        }
                      }
                      
                      return (
                        <div key={section.id || index} className="border-l-2 border-green-500 pl-3">
                          <div style={{
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#111827'
                          }}>
                            {section.name || (() => {
                              if (section.maxRating === 3000 || section.maxRating === '' || !section.maxRating) {
                                return 'Open Section';
                              }
                              // Only add 'U' if maxRating is purely numeric
                              if (/^\d+$/.test(section.maxRating.toString())) {
                                return `U${section.maxRating} Section`;
                              }
                              // For non-numeric ratings, use as-is
                              return `${section.maxRating} Section`;
                            })()}
                          </div>
                          <div style={{
                            fontSize: '18px',
                            fontWeight: 600,
                            color: '#10B981'
                          }}>
                            {priceLabel}{currency}{currentPrice}
                          </div>
                          {priceLabel && (
                            <div style={{
                              fontSize: '12px',
                              color: '#6B7280'
                            }}>
                              Regular price: {currency}{baseFee}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : getDisplayValue(data.ratingSections) ? (
                <div>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#6B7280'
                  }}>Sections</h3>
                  <p style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#111827'
                  }}>{data.ratingSections}</p>
                </div>
              ) : null}

            </div>
          </div>
        )}

        {/* Administrative Info */}
        {(getDisplayValue(data.tournamentDirector) || getDisplayValue(data.checkinTime)) && (
          <div className="bg-chess-cream rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 mb-6 max-w-full sm:max-w-3xl mx-auto">
            <h2 className="text-lg font-semibold text-center mb-4 text-gray-900">Tournament Administration</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Tournament Director */}
              {getDisplayValue(data.tournamentDirector) && (
                <div className={!getDisplayValue(data.checkinTime) ? "md:col-span-2" : ""}>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#6B7280'
                  }}>Tournament Director</h3>
                  <p style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#111827'
                  }}>{data.tournamentDirector}</p>
                </div>
              )}
              
              {/* Check-in Time - moved from basic info */}
              {getDisplayValue(data.checkinTime) && (
                <div className={!getDisplayValue(data.tournamentDirector) ? "md:col-span-2" : ""}>
                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#6B7280'
                  }}>Check-in Time</h3>
                  <p style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#111827'
                  }}>{data.checkinTime}</p>
                </div>
              )}
            </div>
          </div>
        )}

        

      </div>
    </div>
  );
};

// Export hasContent function for tab visibility logic
HomeTabPreview.hasContent = (data) => {
  const getDisplayValue = (value) => value && value.trim() !== '';
  return getDisplayValue(data.eventTitle) || 
         getDisplayValue(data.startDate) || 
         getDisplayValue(data.endDate) ||
         getDisplayValue(data.venueName) ||
         getDisplayValue(data.venueAddress) ||
         getDisplayValue(data.homeOrganizerSection);
};

export default HomeTabPreview; 
